/**
 * Shared comparison + sort-value helpers for every sortable table.
 *
 * `compareValues` lived twice — once in `ui/data-table.tsx`, once in
 * `use-table-controls.ts` — as two byte-identical copies. It's one rule
 * ("how do two cells order against each other"), so it's one function
 * now, and both call sites import it.
 *
 * ── Why the numeric collator matters ──────────────────────────────────
 *
 * Invoice and estimate numbers are strings with a number inside them.
 * A plain `localeCompare` puts `INV-10` *before* `INV-9`, because it
 * compares "1" against "9" one character at a time. The office reads
 * those columns as counters, so `numeric: true` is what they mean by
 * sorted. It costs nothing on columns with no digits in them.
 *
 * The `*SortValue` helpers exist because a sort must run on the value
 * BEHIND the cell, never on the text inside it. `formatDollars` renders
 * "$1,200.00" and "$90.00" — sorted as text, ninety dollars beats twelve
 * hundred. `formatDate` renders "Aug 24, 2026" — sorted as text, April
 * beats January of the following year. Pass the raw column through these
 * and the table sorts by the number and the instant.
 */

/** Case-insensitive, digit-aware collator (see the header note). */
const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

type SortValue = string | number | null | undefined;

const isEmpty = (v: SortValue) => v === null || v === undefined;

/**
 * Compares two sort values ASCENDING: numbers numerically, strings
 * case-insensitively and digit-aware, empty cells last.
 *
 * Anything offering a descending direction must go through
 * `compareInDirection` rather than negating this — see the note there.
 */
export function compareValues(a: SortValue, b: SortValue): number {
    const aEmpty = isEmpty(a);
    const bEmpty = isEmpty(b);
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    if (typeof a === "number" && typeof b === "number") return a - b;
    return collator.compare(String(a), String(b));
}

/**
 * Ascending / descending compare that keeps EMPTY CELLS LAST BOTH WAYS.
 *
 * The obvious implementation — `compareValues(a, b) * dir` — negates the
 * blank handling along with everything else, so one click of "Due, latest
 * first" fills the top of the invoice list with the rows that have no due
 * date at all, and "Balance, biggest first" opens on every invoice whose
 * balance was never recorded instead of on the biggest debt. A blank is not
 * a small value or a large one; it is the absence of one, so it sits out the
 * reversal and stays at the bottom either way.
 */
export function compareInDirection(a: SortValue, b: SortValue, dir: "asc" | "desc"): number {
    if (isEmpty(a) || isEmpty(b)) return compareValues(a, b);
    return compareValues(a, b) * (dir === "asc" ? 1 : -1);
}

/**
 * A date column's sort value: milliseconds since the epoch, or null when the
 * cell is empty or unparseable (so it sorts last rather than as 1970).
 */
export function dateSortValue(value: string | Date | null | undefined): number | null {
    if (value === null || value === undefined || value === "") return null;
    const ms = value instanceof Date ? value.getTime() : Date.parse(value);
    return Number.isNaN(ms) ? null : ms;
}

/**
 * A money column's sort value as a plain number. Accepts the numeric-string
 * form Postgres `numeric` arrives in (`"1200.00"`) as well as an already-
 * parsed number. Blank means blank — it sorts last, it does not become 0,
 * because "no balance recorded" and "$0.00 owed" are different facts.
 */
export function moneySortValue(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === "") return null;
    const n = typeof value === "number" ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}
